import {createAsyncThunk, createSlice} from '@reduxjs/toolkit'

import {DokimoMemberInfo} from '~/utils/helper'
import {req} from '~/utils/request'

export type RechargePrice = {
  currency: string
  symbol: string
  price: number
  display_price: string
}

export type RechargeItem = {
  commodity_id?: number
  goods_id: number
  iap_id?: string
  goods_name?: string
  goods_desc?: string
  goods_price?: number
  display_goods_price: string
  goods_origin_price?: number
  display_goods_origin_price?: string
  status?: number
  is_select?: number
  origin_token_count: number
  presented_token_count?: number
  first_recharge_status?: number
  purchase_restrictions_status?: number
  purchase_limit?: number
  goods_type?: number
  currency?: string
  prices?: RechargePrice[]
  pay_channels?: string[]
}

type ProductListItem = Omit<RechargeItem, 'commodity_id' | 'goods_id'> & {
  commodity_id?: number
  goods_id?: number
}

type ProductListResponse = {
  list?: ProductListItem[]
  pay_channels?: string[]
  member_info?: DokimoMemberInfo
  token_count?: number
}

type ProductListPayload = {
  list: RechargeItem[]
  payChannels: string[]
  memberInfo?: DokimoMemberInfo
}

export type PingPongSessionResponse = {
  expire_ts?: number
  js_sdk_url?: string
  order_id?: string
  pay_amount?: string
  pay_currency?: string
  session_token?: string
}

type CreateRechargeOrderParams = {
  commodity_id: number
  pay_type: 'pingpong'
}

type GetRechargeOrderStatusParams = {
  order_id: string
  pay_channel: 'pingpong'
}

export type RechargeOrderStatusResponse = {
  close_reason?: string
  pay_method?: 'ALIPAY' | 'WECHAT_PAY'
  status: 0 | 1 | 2
  member_info?: DokimoMemberInfo
  token_count?: number
}

type RechargeState = {
  chargeList: RechargeItem[]
  /** 套餐列表请求中（首屏 chargeList 为空时用于骨架屏） */
  chargeListLoading: boolean
  payChannels: string[]
}

const initialState: RechargeState = {
  chargeList: [],
  chargeListLoading: true,
  payChannels: [],
}

export const fetchChargeList = createAsyncThunk<ProductListPayload, void>(
  'fetchChargeList',
  async () => {
    const data: ProductListResponse = await req({
      url: '/api/task/get_product_list',
      params: {},
    })

    if (!Array.isArray(data.list)) {
      const memberInfo =
        data.member_info ??
        (data.token_count != null ? {token_count: data.token_count} : undefined)

      return {
        list: [],
        payChannels: Array.isArray(data.pay_channels) ? data.pay_channels : [],
        memberInfo,
      }
    }

    const list = data.list.reduce<RechargeItem[]>((result, item) => {
      const commodityId = item.commodity_id ?? item.goods_id
      if (commodityId !== undefined) {
        result.push({
          ...item,
          commodity_id: commodityId,
          goods_id: commodityId,
        })
      }
      return result
    }, [])

    const memberInfo =
      data.member_info ??
      (data.token_count != null ? {token_count: data.token_count} : undefined)

    return {
      list,
      payChannels: Array.isArray(data.pay_channels) ? data.pay_channels : [],
      memberInfo,
    }
  }
)

export const createRechargeOrder = createAsyncThunk<
  PingPongSessionResponse,
  CreateRechargeOrderParams
>('createRechargeOrder', async (arg) => {
  return req({
    url: '/api/pay/recharge_token_order',
    params: {...arg},
  })
})

export const getRechargeOrderStatus = createAsyncThunk<
  RechargeOrderStatusResponse,
  GetRechargeOrderStatusParams
>('getRechargeOrderStatus', async (arg) => {
  return req({
    url: '/api/pay/get_order_status',
    params: {...arg},
  })
})

export const rechargeSlice = createSlice({
  name: 'recharge',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchChargeList.pending, (state) => ({
        ...state,
        chargeListLoading: true,
      }))
      .addCase(fetchChargeList.fulfilled, (state, action) => ({
        ...state,
        chargeList: action.payload.list,
        chargeListLoading: false,
        payChannels: action.payload.payChannels,
      }))
      .addCase(fetchChargeList.rejected, (state) => ({
        ...state,
        chargeListLoading: false,
      }))
  },
})

export default rechargeSlice.reducer
